/**
 * Labels for Story moderation (Backlog 060). Access is described by criteria now, not by a plan enum: the row
 * only says whether the Story's criteria gate somebody, how many chapters are open or gated, and the finale mode.
 */

import type { AdminStoryRowDto, StoryAccessRuleDto } from '@api/types/stories'

/** FinaleAccessMode on the server: 0 inherit the Story, 1 open to everyone, 2 its own criteria. */
export const FINALE_ACCESS_MODE_LABELS: Record<number, string> = {
  0: 'inherit',
  1: 'open',
  2: 'own',
}

/**
 * One line for the list: "Access criteria" or "Open", then "N open ch." when some chapters are open while others
 * are gated, "finale: own/open" when the finale does not inherit, and "hidden by display criteria".
 */
export function storyAccessSummary(s: AdminStoryRowDto): string {
  const parts = [s.hasAccessCriteria ? 'Access criteria' : 'Open']
  if (s.gatedChapterCount > 0 && s.openChapterCount > 0) {
    parts.push(`${s.openChapterCount} open ch.`)
  } else if (s.hasAccessCriteria && s.gatedChapterCount === 0 && s.openChapterCount > 0) {
    parts.push('all chapters open')
  } else if (!s.hasAccessCriteria && s.gatedChapterCount > 0) {
    parts.push(`${s.gatedChapterCount} gated ch.`)
  }
  if (s.hasFinale && s.finaleAccessMode !== 0) {
    parts.push(`finale: ${FINALE_ACCESS_MODE_LABELS[s.finaleAccessMode] ?? String(s.finaleAccessMode)}`)
  }
  if (s.hasDisplayCriteria) parts.push('hidden by display criteria')
  return parts.join(' · ')
}

/** Where a story-access rule is set: "Story", "Chapter: Downtown", "Finale". */
export function ruleSourceLabel(r: Pick<StoryAccessRuleDto, 'source' | 'chapterName'>): string {
  switch (r.source) {
    case 'story':
      return 'Story'
    case 'finale':
      return 'Finale'
    default:
      return `Chapter: ${r.chapterName ?? 'unknown'}`
  }
}

/** "Access", "Display". */
export function ruleCategoryLabel(category: StoryAccessRuleDto['category']): string {
  return category === 'display' ? 'Display' : 'Access'
}

export function storyShape(s: AdminStoryRowDto): string {
  const stops = `${s.stopCount} stop${s.stopCount === 1 ? '' : 's'}`
  const chapters = `${s.chapterCount} ch.`
  return s.hasFinale ? `${stops} · ${chapters} · finale` : `${stops} · ${chapters}`
}

export function formatStars(avg?: number | null, count?: number): string {
  if (avg == null) return count ? `— (${count})` : '—'
  return count != null ? `${avg.toFixed(1)} (${count})` : avg.toFixed(1)
}
